import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    clearVehilceReportFilters as clearFilters,
    setVehicleReportSelectedCostCenters as setSelectedCostCenters,
    setVehicleReportSelectedCustomers as setSelectedCustomers,
    setVehicleReportSelectedEmployeeGroups as setSelectedEmployeeGroups,
    setVehicleReportSelectedEmployees as setSelectedEmployees,
    setVehicleReportSelectedVehicles as setSelectedVehicles,
    setVehicleReportSelectedWorkTimeTypes as setSelectedWorkTimeTypes,
    setVehicleReportTimeRange as setTimeRange,
} from "../../store/reportOptionsSlice";
import { MuiDateRangePicker } from "../framework/muiDatepicker";
import { ICostCenterItem } from "../../models/costCenter/costCenterItem";
import { Base } from "../../framework/base";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { useFetch } from "../../hooks/useFetch";
import { getEmployeeGroups } from "../../services/employeeGroupService";
import { ICustomerItem } from "../../models/customer/customerItem";
import { ICustomerListItem } from "../../models/customer/customerListItem";
import { IMuiSelectOption } from "../framework/muiSelect";
import { useTranslation } from "react-i18next";
import { WorkTimeType } from "../../models/workShitTimeSlot/workTimeType";

export const VehicleReportFilters = ({
    vehicles,
    costCenters,
    employees,
    customers,
    hideEmployeeGroup = false,
    workTimeTypes,
}: {
    vehicles: VehicleListItemDto[];
    costCenters?: ICostCenterItem[];
    employees?: EmployeeListItemDto[];
    customers?: ICustomerItem[];
    hideEmployeeGroup?: boolean;
    workTimeTypes?: WorkTimeType[];
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const [employeeGroups, setEmployeeGroups] =
        useState<employeeGroupListItemDto[]>();

    const timeRange = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.timeRange
    );
    const selectedVehicles = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedEmployees
    );
    const selectedEmployeeGroups = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedEmployeeGroups
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedCustomers
    );
    const selectedWorkTimeTypes = useAppSelector(
        (state) => state.reportOptions.vehicleFilters.selectedWorkTimeTypes
    );
    useFetch(
        (signal) => {
            if (!hideEmployeeGroup) {
                return getEmployeeGroups(true, signal);
            }
        },
        {
            onSuccess: (res) =>
                setEmployeeGroups(
                    res.data.filter((d) => !!d.salaryPeriodTypeId)
                ),
        },
        [hideEmployeeGroup]
    );

    const costCenterOptionsWithGroups = useMemo(() => {

        if (!!costCenters) {
            const withGroup = costCenters
                .map((i) => ({
                    value: i.id,
                    label: i.name,
                    group: i.customerName,
                }))
                .sort((a, b) =>
                    // Sort first so internal cost centers are first.
                    Base.stringCompare(a.group, b.group)
                )
                .map((i) => ({
                    ...i,
                    // Add label for internal cost centers after sorting.
                    group: i.group || t("costCenter.internal"),
                }));
            return withGroup;
        }
        return [];

    }, [costCenters]);

    return (
        <Box className="work-time-beta-top-bar">
            <Grid2 container alignItems="center" rowSpacing={1}>
                <Grid2 lg={3} md={4} sm={12} xs={12} marginRight={3}>
                    <Grid2>
                        <MuiDateRangePicker
                            labels={["Aikaväli alkaa", "Aikaväli loppuu"]}
                            value={[
                                new Date(timeRange[0]),
                                new Date(timeRange[1]),
                            ]}
                            onChange={(val) =>
                                dispatch(
                                    setTimeRange([
                                        Base.dayjsToJsonDate(val[0]),
                                        Base.dayjsToJsonDate(val[1]),
                                    ])
                                )
                            }
                        />
                    </Grid2>
                </Grid2>
                {!employeeGroups ? null : (
                    <Grid2
                        md={2}
                        sm={12}
                        xs={12}
                        maxWidth={280}
                        marginRight={3}
                    >
                        <FormControl variant="outlined" fullWidth={true}>
                            <Autocomplete
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={
                                    employeeGroups?.filter((i) =>
                                        selectedEmployeeGroups.includes(i.id)
                                    ) ?? []
                                }
                                multiple={true}
                                disableCloseOnSelect
                                limitTags={2}
                                fullWidth={true}
                                disableClearable={true}
                                noOptionsText={t("common.noOptions")}
                                options={employeeGroups || []}
                                getOptionLabel={(
                                    option: employeeGroupListItemDto
                                ) => option.name}
                                onChange={(
                                    _,
                                    value: employeeGroupListItemDto[]
                                ) =>
                                    dispatch(
                                        setSelectedEmployeeGroups(
                                            value.map((v) => v.id)
                                        )
                                    )
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("employeeGroup.employeeGroup")}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid2>
                )}
                <Grid2 md={2} sm={12} xs={12} maxWidth={280} marginRight={3}>
                    <FormControl variant="outlined" fullWidth={true}>
                        <Autocomplete
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={
                                vehicles?.filter((i) =>
                                    selectedVehicles.includes(i.id)
                                ) ?? []
                            }
                            fullWidth={true}
                            multiple
                            disableCloseOnSelect
                            limitTags={2}
                            noOptionsText={t("common.noOptions")}
                            options={vehicles || []}
                            getOptionLabel={(option: VehicleListItemDto) =>
                                `${option.registerNumber} - ${option.brand}`
                            }
                            onChange={(_, value: VehicleListItemDto[]) => {
                                dispatch(
                                    setSelectedVehicles(value.map((v) => v.id))
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t("vehicle.vehicle")}
                                />
                            )}
                        />
                    </FormControl>
                </Grid2>
                {!customers ? null : (
                    <Grid2
                        md={2}
                        sm={12}
                        xs={12}
                        maxWidth={280}
                        marginRight={3}
                    >
                        <FormControl variant="outlined" fullWidth={true}>
                            <Autocomplete
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={
                                    customers?.filter((i) =>
                                        selectedCustomers?.includes(i.id)
                                    ) ?? []
                                }
                                fullWidth={true}
                                multiple
                                disableCloseOnSelect
                                limitTags={2}
                                noOptionsText={t("common.noOptions")}
                                options={customers || []}
                                getOptionLabel={(option: ICustomerListItem) =>
                                    option.name
                                }
                                onChange={(_, value: ICustomerListItem[]) => {
                                    dispatch(
                                        setSelectedCustomers(
                                            value.map((v) => v.id)
                                        )
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("customer.customer")}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid2>
                )}
                {!costCenters ? null : (
                    <Grid2
                        md={2}
                        sm={12}
                        xs={12}
                        maxWidth={280}
                        marginRight={3}
                    >
                        <FormControl variant="outlined" fullWidth={true}>
                            <Autocomplete
                                isOptionEqualToValue={(a, b) =>
                                    a.value === b.value
                                }
                                value={
                                    costCenterOptionsWithGroups?.filter((i) =>
                                        selectedCostCenters.includes(i.value)
                                    ) ?? []
                                }
                                groupBy={(option) => option.group}
                                fullWidth={true}
                                multiple
                                disableCloseOnSelect
                                limitTags={2}
                                noOptionsText={t("common.noOptions")}
                                options={costCenterOptionsWithGroups || []}
                                getOptionLabel={(option: IMuiSelectOption) =>
                                    option.label
                                }
                                onChange={(_, value: IMuiSelectOption[]) => {
                                    dispatch(
                                        setSelectedCostCenters(
                                            value.map((v) => v.value as string)
                                        )
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("reporting.costCenter")}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid2>
                )}
                {!employees ? null : (
                    <Grid2
                        md={2}
                        sm={12}
                        xs={12}
                        maxWidth={280}
                        marginRight={3}
                    >
                        <FormControl variant="outlined" fullWidth={true}>
                            <Autocomplete
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                value={
                                    employees?.filter((i) =>
                                        selectedEmployees.includes(i.id)
                                    ) ?? []
                                }
                                fullWidth={true}
                                multiple
                                disableCloseOnSelect
                                limitTags={2}
                                noOptionsText={t("common.noOptions")}
                                options={(employees || []).filter(
                                    (e) =>
                                        selectedEmployeeGroups.length === 0 ||
                                        selectedEmployeeGroups.includes(
                                            e.salaryGroupId
                                        )
                                )}
                                getOptionLabel={(option: EmployeeListItemDto) =>
                                    option.fullName
                                }
                                onChange={(_, value: EmployeeListItemDto[]) => {
                                    dispatch(
                                        setSelectedEmployees(
                                            value.map((v) => v.id)
                                        )
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("employee.employee")}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid2>
                )}
                {!workTimeTypes ? null : (
                    <Grid2 md={2} sm={12} xs={12} maxWidth={280}>
                        <FormControl variant="outlined" fullWidth={true}>
                            <Autocomplete
                                isOptionEqualToValue={(a, b) =>
                                    a.workTimeTypeId === b.workTimeTypeId
                                }
                                value={
                                    workTimeTypes?.filter((i) =>
                                        selectedWorkTimeTypes?.includes(
                                            i.workTimeTypeId
                                        )
                                    ) ?? []
                                }
                                fullWidth={true}
                                multiple
                                disableCloseOnSelect
                                limitTags={2}
                                noOptionsText={t("common.noOptions")}
                                options={workTimeTypes || []}
                                getOptionLabel={(option: WorkTimeType) =>
                                    option.name
                                }
                                onChange={(_, value: WorkTimeType[]) => {
                                    dispatch(
                                        setSelectedWorkTimeTypes(
                                            value.map((v) => v.workTimeTypeId)
                                        )
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("workTime.workTimeType")}
                                    />
                                )}
                            />
                        </FormControl>
                    </Grid2>
                )}

                <Grid2 xsOffset="auto" alignItems="self-end">
                    <Button
                        color="secondary"
                        sx={{ color: theme.palette.common.white }}
                        onClick={() => dispatch(clearFilters())}
                        variant={"contained"}
                    >
                        {t("common.clear")}
                    </Button>
                </Grid2>
            </Grid2>
        </Box>
    );
};
